<template>
  <v-dialog v-model="show" fullscreen transition="dialog-bottom-transition">
    <v-card tile>
      <!-- Toolbar -->
      <v-toolbar dark color="primary" dense>
        <v-btn icon dark @click="show = false"><v-icon>mdi-close</v-icon></v-btn>
        <v-toolbar-title>
          <v-icon class="mr-2">mdi-wardrobe-outline</v-icon>
          Stickerei Regal
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn text small @click="openSlotForm()">
          <v-icon left small>mdi-plus</v-icon>
          Slot hinzufügen
        </v-btn>
      </v-toolbar>

      <v-container class="pa-4">
        <v-row v-if="loading">
          <v-col cols="12" class="text-center">
            <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col
            v-for="slot in slots"
            :key="slot.id"
            cols="6" sm="4" md="3" lg="2"
          >
            <v-card
              raised
              style="border-radius: 6px; cursor: pointer;"
              :color="slotColor(slot)"
              dark
              @click="openBookDialog(slot)"
            >
              <v-card-text class="pa-3">
                <!-- Code badge -->
                <div style="font-size: 18px; font-weight: 900; letter-spacing: 1px;">
                  {{ slot.code }}
                </div>
                <!-- Name -->
                <div style="font-size: 11px; opacity: 0.85; line-height: 1.3; min-height: 28px;" class="mt-1">
                  {{ slot.name || slot.productName || '–' }}
                </div>
                <!-- Stock bar -->
                <v-progress-linear
                  class="mt-2"
                  :value="stockPercent(slot)"
                  height="6"
                  rounded
                  color="white"
                  background-color="rgba(255,255,255,0.3)"
                ></v-progress-linear>
                <div class="mt-1" style="font-size: 12px; font-weight: bold; text-align: right;">
                  {{ slot.currentStock }} / {{ slot.maxStock }}
                </div>
              </v-card-text>

              <!-- Actions row -->
              <v-card-actions class="pa-1 pt-0" style="justify-content: flex-end;">
                <v-btn icon x-small @click.stop="printLabel(slot)" :loading="slot._printing">
                  <v-icon small>mdi-printer</v-icon>
                </v-btn>
                <v-btn icon x-small @click.stop="openLog(slot)">
                  <v-icon small>mdi-history</v-icon>
                </v-btn>
                <v-btn icon x-small @click.stop="openSlotForm(slot)">
                  <v-icon small>mdi-pencil</v-icon>
                </v-btn>
                <v-btn icon x-small @click.stop="confirmDelete(slot)">
                  <v-icon small>mdi-delete</v-icon>
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-col>

          <v-col v-if="!loading && slots.length === 0" cols="12">
            <v-alert type="info">Keine Slots vorhanden. Füge den ersten Slot hinzu.</v-alert>
          </v-col>
        </v-row>
      </v-container>
    </v-card>

    <!-- Book Stock Dialog -->
    <v-dialog v-model="bookDialog.show" max-width="400px">
      <v-card v-if="bookDialog.slot">
        <v-card-title>
          <v-icon class="mr-2">mdi-package-variant</v-icon>
          {{ bookDialog.slot.code }} – {{ bookDialog.slot.name || bookDialog.slot.productName || 'Slot' }}
        </v-card-title>
        <v-card-text>
          <div class="mb-3 text-center" style="font-size: 28px; font-weight: bold;">
            {{ bookDialog.slot.currentStock }}
            <span style="font-size: 14px; opacity: 0.6;">/ {{ bookDialog.slot.maxStock }}</span>
          </div>

          <v-btn-toggle v-model="bookDialog.type" mandatory class="mb-4 d-flex">
            <v-btn value="IN" color="success" style="flex:1">
              <v-icon left>mdi-plus</v-icon> Eingang
            </v-btn>
            <v-btn value="OUT" color="error" style="flex:1">
              <v-icon left>mdi-minus</v-icon> Ausgang
            </v-btn>
            <v-btn value="CORRECTION" color="warning" style="flex:1">
              <v-icon left>mdi-tune</v-icon> Korrektur
            </v-btn>
          </v-btn-toggle>

          <v-text-field
            v-model.number="bookDialog.qty"
            :label="bookDialog.type === 'CORRECTION' ? 'Neuer Bestand' : 'Menge'"
            type="number"
            min="0"
            outlined
            dense
            class="mb-2"
          ></v-text-field>

          <v-text-field
            v-model="bookDialog.note"
            label="Notiz (optional)"
            outlined
            dense
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="bookDialog.show = false" :disabled="bookDialog.saving">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveBooking" :loading="bookDialog.saving" :disabled="bookDialog.qty == null">
            Buchen
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Slot Create/Edit Dialog -->
    <v-dialog v-model="slotForm.show" max-width="480px">
      <v-card>
        <v-card-title>{{ slotForm.id ? 'Slot bearbeiten' : 'Neuer Slot' }}</v-card-title>
        <v-card-text>
          <v-form ref="slotForm" v-model="slotForm.valid">
            <v-text-field
              v-model="slotForm.code"
              label="Code * (z.B. A1)"
              outlined dense
              :rules="[v => !!v || 'Pflichtfeld']"
              class="mb-2"
            ></v-text-field>
            <v-text-field
              v-model="slotForm.name"
              label="Name / Bezeichnung"
              outlined dense
              class="mb-2"
            ></v-text-field>
            <v-autocomplete
              v-model="slotForm.productId"
              :items="products"
              item-text="name"
              item-value="id"
              label="Produkt zuweisen (optional)"
              outlined dense
              clearable
              class="mb-2"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.sku }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <v-text-field
              v-model.number="slotForm.maxStock"
              label="Max. Bestand"
              type="number"
              min="0"
              outlined dense
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="slotForm.show = false" :disabled="slotForm.saving">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveSlot" :loading="slotForm.saving" :disabled="!slotForm.valid">
            Speichern
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Log Drawer -->
    <v-dialog v-model="logDialog.show" max-width="500px">
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">mdi-history</v-icon>
          Verlauf – {{ logDialog.slot && logDialog.slot.code }}
          <v-spacer></v-spacer>
          <v-btn icon @click="logDialog.show = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-progress-linear v-if="logDialog.loading" indeterminate></v-progress-linear>
          <v-list dense v-else-if="logDialog.entries.length">
            <v-list-item v-for="entry in logDialog.entries" :key="entry.id">
              <v-list-item-icon class="mr-2">
                <v-icon small :color="logColor(entry.type)">{{ logIcon(entry.type) }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title style="font-size:12px;">
                  <strong>{{ entry.qty > 0 ? '+' : '' }}{{ entry.qty }}</strong>
                  <span class="ml-2 grey--text">{{ entry.type }}</span>
                  <span v-if="entry.note" class="ml-2">– {{ entry.note }}</span>
                </v-list-item-title>
                <v-list-item-subtitle style="font-size:10px;">
                  {{ formatDate(entry.createdAt) }}
                  <span v-if="entry.user" class="ml-1">· {{ entry.user }}</span>
                </v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>
          <div v-else class="pa-4 text-center grey--text">Keine Einträge</div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm -->
    <v-dialog v-model="deleteDialog.show" max-width="360px">
      <v-card>
        <v-card-title>Slot löschen?</v-card-title>
        <v-card-text>
          Slot <strong>{{ deleteDialog.slot && deleteDialog.slot.code }}</strong> wirklich löschen?
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="deleteDialog.show = false">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="deleteSlot" :loading="deleteDialog.deleting">Löschen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="3000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import productionService from "@/services/ProductionService";

export default {
  name: "EmbroideryShelfDialog",
  data: () => ({
    show: false,
    loading: false,
    slots: [],
    products: [],

    bookDialog: {
      show: false,
      slot: null,
      type: "IN",
      qty: null,
      note: "",
      saving: false,
    },

    slotForm: {
      show: false,
      valid: false,
      saving: false,
      id: null,
      code: "",
      name: "",
      productId: null,
      maxStock: 0,
    },

    logDialog: {
      show: false,
      loading: false,
      slot: null,
      entries: [],
    },

    deleteDialog: {
      show: false,
      deleting: false,
      slot: null,
    },

    snackbar: { show: false, text: "", color: "success" },
  }),

  methods: {
    async open() {
      this.show = true;
      await Promise.all([this.fetchSlots(), this.fetchProducts()]);
    },

    async fetchSlots() {
      this.loading = true;
      try {
        const res = await productionService.getEmbroideryShelf();
        this.slots = res.data;
      } catch {
        this.notify("Fehler beim Laden der Slots", "error");
      } finally {
        this.loading = false;
      }
    },

    async fetchProducts() {
      try {
        const res = await productionService.getEmbroideryProducts();
        this.products = res.data;
      } catch { /* non-critical */ }
    },

    // ── Stock color by fill level ─────────────────────────────────────────
    slotColor(slot) {
      if (slot.maxStock === 0) return "grey darken-1";
      const pct = slot.currentStock / slot.maxStock;
      if (pct <= 0) return "grey darken-2";
      if (pct < 0.25) return "error darken-1";
      if (pct < 0.6) return "warning darken-1";
      return "success darken-1";
    },

    stockPercent(slot) {
      if (!slot.maxStock) return 0;
      return Math.min(100, Math.round((slot.currentStock / slot.maxStock) * 100));
    },

    // ── Booking ───────────────────────────────────────────────────────────
    openBookDialog(slot) {
      this.bookDialog.slot = slot;
      this.bookDialog.type = "IN";
      this.bookDialog.qty = null;
      this.bookDialog.note = "";
      this.bookDialog.saving = false;
      this.bookDialog.show = true;
    },

    async saveBooking() {
      this.bookDialog.saving = true;
      try {
        const updated = await productionService.bookEmbroideryStock(this.bookDialog.slot.id, {
          type: this.bookDialog.type,
          qty: this.bookDialog.qty,
          note: this.bookDialog.note || null,
        });
        const idx = this.slots.findIndex(s => s.id === this.bookDialog.slot.id);
        if (idx !== -1) this.$set(this.slots, idx, updated.data);
        this.bookDialog.show = false;
        this.notify("Buchung gespeichert");
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Buchen", "error");
      } finally {
        this.bookDialog.saving = false;
      }
    },

    // ── Slot CRUD ─────────────────────────────────────────────────────────
    openSlotForm(slot = null) {
      this.slotForm.id = slot ? slot.id : null;
      this.slotForm.code = slot ? slot.code : "";
      this.slotForm.name = slot ? (slot.name || "") : "";
      this.slotForm.productId = slot ? slot.productId : null;
      this.slotForm.maxStock = slot ? slot.maxStock : 0;
      this.slotForm.saving = false;
      this.slotForm.show = true;
      this.$nextTick(() => this.$refs.slotForm && this.$refs.slotForm.resetValidation());
    },

    async saveSlot() {
      if (!this.$refs.slotForm.validate()) return;
      this.slotForm.saving = true;
      const payload = {
        code: this.slotForm.code,
        name: this.slotForm.name || null,
        productId: this.slotForm.productId || null,
        maxStock: this.slotForm.maxStock || 0,
      };
      try {
        if (this.slotForm.id) {
          await productionService.updateEmbroiderySlot(this.slotForm.id, payload);
          this.notify("Slot aktualisiert");
        } else {
          await productionService.createEmbroiderySlot(payload);
          this.notify("Slot erstellt");
        }
        this.slotForm.show = false;
        await this.fetchSlots();
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Speichern", "error");
      } finally {
        this.slotForm.saving = false;
      }
    },

    confirmDelete(slot) {
      this.deleteDialog.slot = slot;
      this.deleteDialog.deleting = false;
      this.deleteDialog.show = true;
    },

    async deleteSlot() {
      this.deleteDialog.deleting = true;
      try {
        await productionService.deleteEmbroiderySlot(this.deleteDialog.slot.id);
        this.deleteDialog.show = false;
        this.notify("Slot gelöscht");
        await this.fetchSlots();
      } catch {
        this.notify("Fehler beim Löschen", "error");
      } finally {
        this.deleteDialog.deleting = false;
      }
    },

    // ── Log ───────────────────────────────────────────────────────────────
    async openLog(slot) {
      this.logDialog.slot = slot;
      this.logDialog.entries = [];
      this.logDialog.loading = true;
      this.logDialog.show = true;
      try {
        const res = await productionService.getEmbroiderySlotLog(slot.id);
        this.logDialog.entries = res.data;
      } catch {
        this.notify("Fehler beim Laden des Verlaufs", "error");
      } finally {
        this.logDialog.loading = false;
      }
    },

    logColor(type) {
      return type === 'IN' ? 'success' : type === 'OUT' ? 'error' : 'warning';
    },
    logIcon(type) {
      return type === 'IN' ? 'mdi-arrow-down' : type === 'OUT' ? 'mdi-arrow-up' : 'mdi-tune';
    },

    formatDate(dt) {
      if (!dt) return '';
      return new Date(dt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    async printLabel(slot) {
      this.$set(slot, '_printing', true);
      try {
        await productionService.printEmbroideryShelfLabel(slot.id);
        this.notify(`Label für ${slot.code} gedruckt`);
      } catch (err) {
        this.notify(err.response?.data?.error || "Fehler beim Drucken", "error");
      } finally {
        this.$set(slot, '_printing', false);
      }
    },

    notify(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },
  },
};
</script>
